import React, { useState } from 'react';
import { 
  XIcon, 
  CreditCardIcon, 
  DollarSignIcon,
  CheckCircleIcon,
  AlertCircleIcon
} from 'lucide-react';
import { billingAPI } from '../../lib/api';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  billing: {
    id: string;
    totalAmount: number;
    paymentStatus: string;
    patient: {
      user: {
        firstName: string;
        lastName: string;
      };
    };
  } | null;
}

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash', icon: DollarSignIcon },
  { value: 'CREDIT_CARD', label: 'Credit Card', icon: CreditCardIcon },
  { value: 'DEBIT_CARD', label: 'Debit Card', icon: CreditCardIcon },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer', icon: CreditCardIcon },
  { value: 'INSURANCE', label: 'Insurance', icon: CheckCircleIcon }
];

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  billing
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: billing?.totalAmount || 0,
    paymentMethod: 'CASH',
    transactionId: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (billing) {
      setFormData(prev => ({
        ...prev,
        amount: Number(billing.totalAmount)
      }));
    }
  }, [billing]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Payment amount must be greater than 0';
    }

    if (formData.amount > Number(billing?.totalAmount || 0)) {
      newErrors.amount = 'Payment amount cannot exceed the bill total';
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
    }

    if (['CREDIT_CARD', 'DEBIT_CARD', 'BANK_TRANSFER'].includes(formData.paymentMethod) && !formData.transactionId.trim()) {
      newErrors.transactionId = 'Transaction ID is required for this payment method';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!billing || !validateForm()) return;

    try {
      setLoading(true);
      setErrors({});

      const response = await billingAPI.processPayment(billing.id, {
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        transactionId: formData.transactionId || undefined,
        notes: formData.notes || undefined
      });

      if (response.success) {
        onSuccess();
        onClose();
        resetForm();
      } else {
        setErrors({ submit: 'Failed to process payment' });
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setErrors({ submit: 'Failed to process payment' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: billing?.totalAmount || 0,
      paymentMethod: 'CASH',
      transactionId: '',
      notes: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const generateTransactionId = () => {
    const prefix = formData.paymentMethod === 'CASH' ? 'CASH' : 'TXN';
    const randomId = Math.random().toString(36).substr(2, 9).toUpperCase();
    setFormData({ ...formData, transactionId: `${prefix}-${randomId}` });
  };

  const remainingBalance = billing ? Number(billing.totalAmount) - formData.amount : 0;
  const isPartialPayment = remainingBalance > 0;
  const isFullPayment = remainingBalance === 0;

  if (!isOpen || !billing) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSignIcon className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Process Payment</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Patient Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-1">Patient</h3>
            <p className="text-lg text-gray-700">
              {billing.patient.user.firstName} {billing.patient.user.lastName}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Total Bill: <span className="font-medium">${Number(billing.totalAmount).toFixed(2)}</span>
            </p>
          </div>

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                max={billing.totalAmount}
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className={`input pl-8 ${errors.amount ? 'border-red-500' : ''}`}
                placeholder="0.00"
              />
            </div>
            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
            
            {/* Payment Status Indicator */}
            {formData.amount > 0 && (
              <div className="mt-2 p-2 rounded-lg bg-blue-50">
                {isFullPayment ? (
                  <div className="flex items-center text-green-700">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Full payment - Bill will be marked as PAID</span>
                  </div>
                ) : isPartialPayment ? (
                  <div className="flex items-center text-yellow-700">
                    <AlertCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      Partial payment - Remaining balance: ${remainingBalance.toFixed(2)}
                    </span>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <div className="grid grid-cols-1 gap-2">
              {PAYMENT_METHODS.map((method) => {
                const IconComponent = method.icon;
                return (
                  <label
                    key={method.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.paymentMethod === method.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.value}
                      checked={formData.paymentMethod === method.value}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                      className="sr-only"
                    />
                    <IconComponent className="h-5 w-5 text-gray-600 mr-3" />
                    <span className="text-sm font-medium text-gray-900">{method.label}</span>
                  </label>
                );
              })}
            </div>
            {errors.paymentMethod && <p className="text-red-500 text-sm mt-1">{errors.paymentMethod}</p>}
          </div>

          {/* Transaction ID */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Transaction ID {['CREDIT_CARD', 'DEBIT_CARD', 'BANK_TRANSFER'].includes(formData.paymentMethod) && '*'}
              </label>
              <button
                type="button"
                onClick={generateTransactionId}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Generate
              </button>
            </div>
            <input
              type="text"
              value={formData.transactionId}
              onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
              className={`input ${errors.transactionId ? 'border-red-500' : ''}`}
              placeholder="Enter or generate transaction ID"
            />
            {errors.transactionId && <p className="text-red-500 text-sm mt-1">{errors.transactionId}</p>}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="input"
              placeholder="Additional payment notes..."
            />
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.amount <= 0}
              className="btn-primary"
            >
              {loading ? 'Processing...' : `Process Payment`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal; 